import React, { useState, useEffect } from 'react';
import {
  PaymentElement,
  AddressElement,
  LinkAuthenticationElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faSpinner } from '@fortawesome/free-solid-svg-icons';

interface StripeCheckoutFormProps {
  onSuccess: () => void;
  onError: (error: string) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

const StripeCheckoutForm: React.FC<StripeCheckoutFormProps> = ({
  onSuccess,
  onError,
  isLoading,
  setIsLoading
}) => {
  const stripe = useStripe();
  const elements = useElements();
  
  const [email, setEmail] = useState('');
  const [messages, setMessages] = useState<string[]>([]);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (!stripe) {
      return;
    }

    const clientSecret = new URLSearchParams(window.location.search).get(
      'payment_intent_client_secret'
    );

    if (!clientSecret) {
      return;
    }

    stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
      switch (paymentIntent?.status) {
        case 'succeeded':
          setMessages(['Payment succeeded!']);
          onSuccess();
          break;
        case 'processing':
          setMessages(['Your payment is processing.']);
          break;
        case 'requires_payment_method':
          setMessages(['Your payment was not successful, please try again.']);
          break;
        default:
          setMessages(['Something went wrong.']);
          break;
      }
    });
  }, [stripe, onSuccess]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded
      return;
    }

    setIsLoading(true);
    setMessages([]);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Make sure to change this to your payment completion page
        return_url: `${window.location.origin}/checkout/success`,
        receipt_email: email,
      },
    });

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.
    if (error) {
      if (error.type === 'card_error' || error.type === 'validation_error') {
        onError(error.message || 'Payment failed');
      } else {
        onError('An unexpected error occurred.');
      }
    }

    setIsLoading(false);
  };

  const paymentElementOptions = {
    layout: 'tabs' as const,
  };

  const addressElementOptions = {
    mode: 'shipping' as const,
    allowedCountries: ['US', 'CA'],
    autocomplete: {
      mode: 'automatic' as const,
    },
  };

  return (
    <form id="payment-form" onSubmit={handleSubmit} className="space-y-6">
      {/* Link Authentication Element */}
      <div className="bg-background-light rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">Account Information</h3>
        <LinkAuthenticationElement
          id="link-authentication-element"
          onChange={(event) => {
            if (event.value?.email) {
              setEmail(event.value.email);
            }
          }}
        />
      </div>

      {/* Address Element */}
      <div className="bg-background-light rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">Shipping Address</h3>
        <AddressElement
          id="address-element"
          options={addressElementOptions}
          onChange={(event) => {
            setIsComplete(event.complete);
          }}
        />
      </div>

      {/* Payment Element */}
      <div className="bg-background-light rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">Payment Information</h3>
        <PaymentElement 
          id="payment-element" 
          options={paymentElementOptions}
        />
      </div>

      {/* Error Messages */}
      {messages.length > 0 && (
        <div className="bg-red-900/20 text-red-400 p-4 rounded-lg">
          {messages.map((message, index) => (
            <div key={index}>{message}</div>
          ))}
        </div>
      )}

      {/* Submit Button */}
      <button
        disabled={isLoading || !stripe || !elements}
        id="submit"
        className="w-full py-4 bg-accent text-white rounded-lg font-medium hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
      >
        {isLoading ? (
          <span className="flex items-center">
            <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
            Processing Payment...
          </span>
        ) : (
          <span className="flex items-center">
            <FontAwesomeIcon icon={faLock} className="mr-2" />
            Complete Payment
          </span>
        )}
      </button>

      {/* Security Notice */}
      <div className="text-xs text-center text-secondary flex items-center justify-center">
        <FontAwesomeIcon icon={faLock} className="mr-1 h-3 w-3" />
        <span>Your payment information is secure and encrypted</span>
      </div>
    </form>
  );
};

export default StripeCheckoutForm; 